import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { ConfirmDialog, Toast, useToast, useConfirm } from '../components/ConfirmDialog';
import { MdAssignment, MdPeople, MdEdit, MdDelete, MdSearch, MdFilterList, MdAdd, MdFolderOpen, MdClose } from 'react-icons/md';
import './Projects.css';

const Projects = () => {
  const { user } = useContext(AuthContext);
  const { toasts, addToast, removeToast } = useToast();
  const { dialog, confirm } = useConfirm();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProjectForAssign, setSelectedProjectForAssign] = useState(null);
  const [selectedProjectForDetails, setSelectedProjectForDetails] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '', color: '#3B82F6' });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [allUsers, setAllUsers] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState('');

  useEffect(() => {
    fetchProjects();
    fetchAllUsers();
  }, []);

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get('/api/users');
      setAllUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, statusFilter]);

  const fetchProjects = async () => {
    try {
      const [projectsRes, tasksRes] = await Promise.all([
        axios.get('/api/projects'),
        axios.get('/api/tasks')
      ]);
      
      const allProjects = projectsRes.data;
      const allTasks = tasksRes.data;
      
      // For regular users, show projects where they are members OR have assigned tasks
      if (user?.role === 'admin') {
        setProjects(allProjects);
      } else {
        const userTasks = allTasks.filter(t => t.assigned_to === user?.id);
        const userProjectIds = [...new Set(userTasks.map(t => t.project_id).filter(Boolean))];
        
        // Also include projects where user is a member
        const userProjects = allProjects.filter(p => {
          return userProjectIds.includes(p.id) || 
                 (p.members && p.members.some(m => m.id === user?.id));
        });
        
        setProjects(userProjects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    setFilteredProjects(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(`/api/projects/${editingProject.id}`, formData);
      } else {
        await axios.post('/api/projects', formData);
      }
      setShowModal(false);
      setEditingProject(null);
      setFormData({ name: '', description: '', color: '#3B82F6' });
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      color: project.color || '#3B82F6'
    });
    setShowModal(true);
  };

  const handleDelete = async (projectId) => {
    const ok = await confirm('Delete Project', 'Are you sure you want to delete this project? This action cannot be undone.', { confirmText: 'Delete', confirmColor: '#EF4444' });
    if (!ok) return;
    try {
      await axios.delete(`/api/projects/${projectId}`);
      addToast('Project deleted successfully', 'success');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      addToast('Failed to delete project. It may have associated tasks.', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData({ name: '', description: '', color: '#3B82F6' });
  };

  const handleOpenAssignModal = async (project) => {
    setSelectedProjectForAssign(project);
    try {
      const res = await axios.get(`/api/projects/${project.id}`);
      setProjectMembers(res.data.members || []);
    } catch (error) {
      console.error('Error fetching project members:', error);
      setProjectMembers([]);
    }
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedProjectForAssign(null);
    setProjectMembers([]);
    setSelectedUserToAdd('');
  };

  const handleAddUserToProject = async () => {
    if (!selectedUserToAdd) {
      addToast('Please select a user', 'warning');
      return;
    }
    try {
      await axios.post(`/api/projects/${selectedProjectForAssign.id}/members`, { user_id: parseInt(selectedUserToAdd), role: 'member' });
      const res = await axios.get(`/api/projects/${selectedProjectForAssign.id}`);
      setProjectMembers(res.data.members || []);
      setSelectedUserToAdd('');
      fetchProjects();
    } catch (error) {
      console.error('Error adding user to project:', error);
      addToast('Failed to add user to project', 'error');
    }
  };

  const handleRemoveUserFromProject = async (userId) => {
    const ok = await confirm('Remove Member', 'Remove this user from the project?', { confirmText: 'Remove', confirmColor: '#EF4444' });
    if (!ok) return;
    try {
      await axios.delete(`/api/projects/${selectedProjectForAssign.id}/members/${userId}`);
      const res = await axios.get(`/api/projects/${selectedProjectForAssign.id}`);
      setProjectMembers(res.data.members || []);
      fetchProjects();
    } catch (error) {
      console.error('Error removing user from project:', error);
      addToast('Failed to remove user from project', 'error');
    }
  };

  const handleOpenProjectDetails = async (project) => {
    setSelectedProjectForDetails(project);
    try {
      const [projectRes, tasksRes] = await Promise.all([
        axios.get(`/api/projects/${project.id}`),
        axios.get(`/api/tasks?project_id=${project.id}`)
      ]);
      setProjectMembers(projectRes.data.members || []);
      setProjectTasks(tasksRes.data || []);
    } catch (error) {
      console.error('Error fetching project details:', error);
      setProjectMembers([]);
      setProjectTasks([]);
    }
    setShowProjectDetailsModal(true);
  };

  const handleCloseProjectDetails = () => {
    setShowProjectDetailsModal(false);
    setSelectedProjectForDetails(null);
    setProjectMembers([]);
    setProjectTasks([]);
  };

  if (loading) {
    return (
      <div className="app-container">
        <Sidebar />
        <div className="main-content projects-main">
          <div className="projects-page">
            <div className="page-header">
              <h1>Projects</h1>
            </div>
            <div className="skeleton-grid">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-header"></div>
                  <div className="skeleton-text"></div>
                  <div className="skeleton-text short"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <Toast toasts={toasts} removeToast={removeToast} />
      <ConfirmDialog {...dialog} />
      <div className="main-content projects-main">
        <div className="projects-page">
          <div className="page-header">
            <div className="header-content">
              <h1><MdFolderOpen /> {user?.role === 'admin' ? 'Projects' : 'My Projects'}</h1>
              <p className="header-subtitle">{user?.role === 'admin' ? 'Organize and manage your projects' : 'Projects with your assigned tasks'}</p>
            </div>
            <div className="header-actions">
              <span className="count-badge">{filteredProjects.length} {filteredProjects.length === 1 ? 'Project' : 'Projects'}</span>
              {user?.role === 'admin' && (
                <button className="btn-primary" onClick={() => setShowModal(true)}>
                  <MdAdd /> New Project
                </button>
              )}
            </div>
          </div>

          <div className="filters-bar">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <MdFilterList className="filter-icon" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="on-hold">On Hold</option>
              </select>
            </div>
          </div>

          {filteredProjects.length === 0 ? (
            <div className="empty-state">
              <p>{searchTerm || statusFilter !== 'all' ? 'No projects match your filters' : 'No projects yet. Create your first project!'}</p>
            </div>
          ) : (
            <div className="projects-grid">
              {filteredProjects.map(project => (
                <div key={project.id} className="project-card" onClick={() => user?.role !== 'admin' && handleOpenProjectDetails(project)} style={{ cursor: user?.role !== 'admin' ? 'pointer' : 'default' }}>
                  <div className="project-header" style={{ borderLeftColor: project.color }}>
                    <h3>{project.name}</h3>
                    <div className="project-actions">
                      <span className={`status-badge status-${project.status}`}>
                        {project.status}
                      </span>
                      {user?.role === 'admin' && (
                        <>
                          <button className="icon-btn assign" onClick={() => handleOpenAssignModal(project)} title="Assign Users">
                            <MdPeople />
                          </button>
                          <button className="icon-btn edit" onClick={() => handleEdit(project)} title="Edit">
                            <MdEdit />
                          </button>
                          <button className="icon-btn delete" onClick={() => handleDelete(project.id)} title="Delete">
                            <MdDelete />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="project-description">{project.description || 'No description'}</p>
                  <div className="project-stats">
                    <span><MdAssignment /> {project.task_count || 0} tasks</span>
                    <span><MdPeople /> {project.member_count || 0} members</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showModal && (
            <div className="modal-overlay" onClick={handleCloseModal}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h2>{editingProject ? 'Edit Project' : 'Create New Project'}</h2>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>Project Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="4"
                    />
                  </div>
                  <div className="form-group">
                    <label>Color</label>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    />
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      {editingProject ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showAssignModal && selectedProjectForAssign && (
            <div className="modal-overlay" onClick={handleCloseAssignModal}>
              <div className="modal assign-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Assign Users to {selectedProjectForAssign.name}</h2>
                  <button className="close-btn" onClick={handleCloseAssignModal}>
                    <MdClose />
                  </button>
                </div>
                
                <div className="assign-content">
                  <div className="add-user-section">
                    <h3>Add User to Project</h3>
                    <div className="form-group">
                      <select 
                        value={selectedUserToAdd}
                        onChange={(e) => setSelectedUserToAdd(e.target.value)}
                      >
                        <option value="">Select a user...</option>
                        {allUsers.map(u => {
                          const isAlreadyMember = projectMembers.some(m => m.id === u.id);
                          return (
                            <option key={u.id} value={u.id} disabled={isAlreadyMember}>
                              {u.full_name || u.username} {isAlreadyMember ? '(Already member)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <button className="btn-primary" onClick={handleAddUserToProject}>
                      <MdAdd /> Add User
                    </button>
                  </div>

                  <div className="members-section">
                    <h3>Project Members ({projectMembers.length})</h3>
                    {projectMembers.length === 0 ? (
                      <p className="empty-members">No members assigned yet</p>
                    ) : (
                      <div className="members-list">
                        {projectMembers.map(member => (
                          <div key={member.id} className="member-card">
                            <div className="member-header">
                              <div className="member-info">
                                <div className="member-avatar">
                                  {member.avatar_url ? (
                                    <img src={member.avatar_url} alt={member.full_name || member.username} />
                                  ) : (
                                    <div className="avatar-placeholder">
                                      {(member.full_name || member.username)[0].toUpperCase()}
                                    </div>
                                  )}
                                </div>
                                <div className="member-details">
                                  <p className="member-name">{member.full_name || member.username}</p>
                                  <p className="member-email">{member.email}</p>
                                </div>
                              </div>
                              <button 
                                className="btn-remove"
                                onClick={() => handleRemoveUserFromProject(member.id)}
                                title="Remove from project"
                              >
                                <MdClose />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="btn-secondary" onClick={handleCloseAssignModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {showProjectDetailsModal && selectedProjectForDetails && (
            <div className="modal-overlay" onClick={handleCloseProjectDetails}>
              <div className="modal assign-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{selectedProjectForDetails.name}</h2>
                  <button className="close-btn" onClick={handleCloseProjectDetails}>
                    <MdClose />
                  </button>
                </div>
                
                <div className="assign-content">
                  <div className="project-info-section">
                    <h3>Project Information</h3>
                    <p className="project-desc">{selectedProjectForDetails.description || 'No description'}</p>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Status</span>
                        <span className={`status-badge status-${selectedProjectForDetails.status}`}>
                          {selectedProjectForDetails.status}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Tasks</span>
                        <span className="info-value">{projectTasks.length}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Members</span>
                        <span className="info-value">{projectMembers.length}</span>
                      </div>
                    </div>
                  </div>

                  <div className="members-section">
                    <h3>Team Members ({projectMembers.length})</h3>
                    {projectMembers.length === 0 ? (
                      <p className="empty-members">No members assigned yet</p>
                    ) : (
                      <div className="members-list">
                        {projectMembers.map(member => (
                          <div key={member.id} className="member-item">
                            <div className="member-info">
                              <div className="member-avatar">
                                {member.avatar_url ? (
                                  <img src={member.avatar_url} alt={member.full_name || member.username} />
                                ) : (
                                  <div className="avatar-placeholder">
                                    {(member.full_name || member.username)[0].toUpperCase()}
                                  </div>
                                )}
                              </div>
                              <div className="member-details">
                                <p className="member-name">{member.full_name || member.username}</p>
                                <p className="member-email">{member.email}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="tasks-section">
                    <h3>Project Tasks ({projectTasks.length})</h3>
                    {projectTasks.length === 0 ? (
                      <p className="empty-members">No tasks in this project</p>
                    ) : (
                      <div className="tasks-list">
                        {projectTasks.map(task => (
                          <div key={task.id} className="task-item-detail">
                            <div className="task-header-detail">
                              <h4>{task.title}</h4>
                              <span className={`priority-badge priority-${task.priority}`}>
                                {task.priority}
                              </span>
                            </div>
                            <p className="task-desc">{task.description}</p>
                            <div className="task-footer-detail">
                              <span className={`status-badge status-${task.status}`}>
                                {task.status}
                              </span>
                              {task.assigned_to_name && (
                                <span className="assigned-to">👤 {task.assigned_to_name}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="btn-secondary" onClick={handleCloseProjectDetails}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Projects;
